/**
 * @fileoverview Pure constants for the Projects domain.
 *
 * Default values for new projects and configuration for duplication.
 * For helper functions, see ./helpers.ts.
 */

/**
 * Banned character names - names that AI tends to overuse and should be avoided
 * when creating new characters. These are checked during name ideation.
 */
export const BANNED_CHARACTER_NAMES: string[] = [
  'Elara', 'Thorne', 'Silas', 'Vance', 'Vane', 'Kaelen', 'Chloe',
  'Elias', 'Marcus','Maya'
];

/**
 * Blocked writing terms - words and phrases that should never appear in AI-generated prose.
 * These are stylistic issues rather than character names.
 */
export const BLOCKED_WRITING_TERMS: string[] = [
  'clinical', 'tapestry', 'symphony', 'sternum'
];

/**
 * Default writing blocklist - names and terms that should never appear in AI-generated prose.
 * This list is copied to new projects during creation. Users can modify their project's
 * blocklist afterward. Combines banned names and blocked terms.
 */
export const DEFAULT_WRITING_BLOCKLIST: string[] = [
  ...BANNED_CHARACTER_NAMES,
  ...BLOCKED_WRITING_TERMS,
];

export const DEFAULT_LANGUAGE = 'English';
export const DEFAULT_STORY_TYPE = 'Short Story';
export const DEFAULT_PLOT_TYPE = 'None';
export const DEFAULT_POV = 'None';

export const DEFAULT_STYLE_GUIDE_CONTENT = 'Default writing style. Clear, concise, and engaging.';
export const DEFAULT_STYLE_GUIDE_NAME = 'Default';

// Validation Constants
export const MIN_BLURB_LENGTH = 10;
export const MAX_BLURB_LENGTH = 1000;
export const MIN_TITLE_LENGTH = 3;
export const MAX_TITLE_LENGTH = 100;

/**
 * UI labels and descriptions for the duplication options.
 */
export const DUPLICATION_OPTION_DETAILS = [
    { id: 'storySetup', label: 'Story Setup', description: 'Blurb, genre, POV, etc.' },
    { id: 'plot', label: 'Story Architect', description: 'The active story outline and any generated options.' },
    { id: 'characters', label: 'Characters', description: 'All major, minor, and extra characters.' },
    { id: 'worldElements', label: 'World-building Elements', description: 'All locations, cultures, systems, and history.' },
    { id: 'chapterOutlines', label: 'Chapter & Scene Outlines', description: 'The structure of all chapters and their scenes.' },
    { id: 'chapterContent', label: 'Chapter Writing Content', description: 'The written prose inside each chapter.' },
    { id: 'styleGuides', label: 'Style Guides', description: 'Your full library of custom and template style guides.' },
] as const;
